// src/app/modules/user/components/patient/forms/ManualTimeTrackedForm.tsx
import React from 'react'
import {Modal, Form, Row, Col} from 'react-bootstrap'
import {Formik, Form as FormikForm, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import DateTimePickerField from '../DateTimePickerField'
import PrimaryButton from '../common/buttons/PrimaryButton'
import Astericsign from './Astericsign'
import { FiCalendar } from 'react-icons/fi'

export interface TimeTrackedValues {
  description: string
  from: Date | null
  to: Date | null
}

interface ManualTimeTrackedProps {
  show: boolean
  onHide: () => void
  onSubmit: (values: TimeTrackedValues, resetForm: () => void) => Promise<void> | void
}

const validationSchema = Yup.object().shape({
  description: Yup.string().max(1024, 'Notes must be at most 1024 characters'),
  from: Yup.date()
    .nullable()
    .required('The Start time is required.')
    .test('not-in-future', 'The Start time cannot be in the future.', (value) =>
      value ? value.getTime() <= Date.now() : false
    ),
  to: Yup.date()
    .nullable()
    .required('The End time is required.')
    .min(Yup.ref('from'), 'End time must be greater than start time')
    .test('not-in-future', 'The End time cannot be in the future.', (value) =>
      value ? value.getTime() <= Date.now() : false
    )
    .test('not-equal', 'The Start time cannot be the same as End time.', function (value) {
      const {from} = this.parent as TimeTrackedValues
      return value && from ? new Date(value).getTime() !== new Date(from).getTime() : false
    }),
})

const ManualTimeTrackedForm: React.FC<ManualTimeTrackedProps> = ({show, onHide, onSubmit}) => (
  <Modal show={show} onHide={onHide} centered size='lg'>
    <Modal.Header closeButton className='border-0 pb-0'>
      <Modal.Title className='modal-title fs-5'>Add Manual Time Entry</Modal.Title>
    </Modal.Header>

    <Formik<TimeTrackedValues>
      initialValues={{description: '', from: null, to: null}}
      validationSchema={validationSchema}
      onSubmit={async (values, {setSubmitting, resetForm}) => {
        await onSubmit(values, resetForm)
        setSubmitting(false)
        onHide()
      }}
    >
      {({values, isSubmitting}) => {
        let durationText = ''
        if (values.from && values.to) {
          const fromDate = new Date(values.from)
          const toDate = new Date(values.to)
          if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
            const diffMs = toDate.getTime() - fromDate.getTime()
            const minutes = Math.round(diffMs / 60000)
            durationText = minutes >= 0 ? `${minutes} minutes` : '—'
          }
        }

        return (
          <FormikForm>
            <Modal.Body className='pt-1'>
              <Row className='g-3'>
                <Col md={6}>
                  <Form.Group controlId='from'>
                      <Form.Label className='fw-medium'><FiCalendar /> Start Time  <Astericsign /></Form.Label>
                      <Field
                        name='from'
                        component={DateTimePickerField}
                        className='form-control rounded-1'
                        maxDate={new Date()}
                      />
                    <ErrorMessage name='from' component='div' className='text-danger small mt-1' />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId='to'>
                    <Form.Label className='fw-medium'><FiCalendar /> End Time  <Astericsign /></Form.Label>
                    <Field
                      name='to'
                      component={DateTimePickerField}
                      className='form-control rounded-1'
                      maxDate={new Date()}
                    />
                    <ErrorMessage name='to' component='div' className='text-danger small mt-1' />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <div className='text-end small text-muted'>
                    <strong>Duration:</strong> {durationText}
                  </div>
                </Col>

                <Col xs={12}>
                  <Form.Group controlId='description'>
                    <Form.Label className='fw-medium'>Notes</Form.Label>
                    <Field
                      as='textarea'
                      name='description'
                      className='form-control rounded-1'
                      placeholder='Add context or details about this time entry...'
                      rows={3}
                      maxLength={1024}
                    />
                    <div className='text-end small text-muted mt-1'>
                      <Field name='description'>
                        {({field}: any) => `${field.value.length}/1024 characters`}
                      </Field>
                    </div>
                    <ErrorMessage
                      name='description'
                      component='div'
                      className='text-danger small mt-1'
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer className='border-0 pt-0'>
              <PrimaryButton
                color='secondary'
                onClick={onHide} 
              >
              Cancel
              </PrimaryButton>
              <PrimaryButton type='submit' disabled={isSubmitting} color='primary'>
                {isSubmitting ? 'Saving...' : 'Save Entry'}
              </PrimaryButton>
            </Modal.Footer>
          </FormikForm>
        )
      }}
    </Formik>
  </Modal>
)

export default ManualTimeTrackedForm
